import { OkthxLogo } from "@/components/icons/Logo.tsx";
import { Button } from "@/components/ui/button.tsx";
import { useCurrentUserContext } from "@/hooks/useUser.tsx";
import { tourHidesAccess } from "@/shared/connectors/tourAccess.ts";
import { Bell, CalendarDays, Inbox, MessageCircle } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { TourStepProps } from "../types.ts";

type Capability = {
  Icon: LucideIcon;
  title: string;
  description: string;
};

/**
 * Deliberately phrased as things okthx does for you, not features it has, and
 * kept to the same four jobs as the assistant's first text message so a new
 * user hears one story about what they just signed up for.
 */
const capabilities: Capability[] = [
  {
    Icon: CalendarDays,
    title: "Your calendar",
    description: "Find time with anyone, book it, and handle the back and forth.",
  },
  {
    Icon: Inbox,
    title: "Your inbox",
    description: "Surface what actually needs you, and draft the replies in your voice.",
  },
  {
    Icon: MessageCircle,
    title: "On your behalf",
    description: "Text people to sort out plans, and call places to book things.",
  },
  {
    Icon: Bell,
    title: "Before you ask",
    description: "What's on tomorrow, prep before meetings, reminders that land on time.",
  },
];

export const WelcomeStep = ({ onNext }: TourStepProps) => {
  const { userContext } = useCurrentUserContext();
  // No inbox or calendar promised to an account that cannot reach one.
  const hides = tourHidesAccess(userContext?.user.connectors);
  const shown = capabilities.filter(
    ({ Icon }) => !(hides.inbox && Icon === Inbox) && !(hides.calendar && Icon === CalendarDays),
  );
  const firstName = userContext?.user.userContact?.displayShortName;

  return (
    <div className="flex flex-col gap-7 w-full">
      <div className="flex flex-col items-center gap-4 text-center">
        <OkthxLogo className="size-10" />
        <div className="flex flex-col gap-2">
          <h2 className="font-serif text-3xl leading-tight">
            {firstName ? `Hi ${firstName}, I'm Okthx` : "Hi there, I'm Okthx"}
          </h2>
          <p className="text-muted-foreground">Your assistant, with a computer and a phone.</p>
        </div>
      </div>

      <ul className="flex flex-col gap-3">
        {shown.map(({ Icon, title, description }) => (
          <li key={title} className="flex gap-3.5 items-start">
            <div className="bg-accent rounded-xl p-2.5 shrink-0">
              <Icon className="size-[18px] text-primary" strokeWidth={1.75} />
            </div>
            <div className="flex flex-col gap-0.5 pt-1">
              <span className="font-medium text-sm leading-5">{title}</span>
              <span className="text-sm leading-5 text-muted-foreground">{description}</span>
            </div>
          </li>
        ))}
      </ul>

      <div className="bg-brand-50 rounded-2xl px-5 py-4 flex flex-col gap-1.5">
        <span className="font-medium text-sm">The main thing: I learn.</span>
        <p className="text-sm leading-5 text-muted-foreground">
          When I draft something that doesn't sound like you, or book something a way you'd rather I
          hadn't, tell me. I'll work that way from then on. The people who get the most out of me
          are the ones who coach me early.
        </p>
      </div>

      <div className="flex justify-center">
        <Button variant="primary" onClick={onNext} data-statsig-name="nux-welcome-start">
          Let's go
        </Button>
      </div>
    </div>
  );
};
